import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type {
  IGame,
  IInputMessage,
  IMessage,
  IMessageWithSocialValue,
  ISocialInviteMessage,
  ISocialMessage,
  IStickerPack,
  IUserDetailed,
} from '@dev-fast/types';
import { ChatMessageType, CS2_NICKNAME, EAccountType, EWalletType, SocialType } from '@dev-fast/types';
import { Store } from '@ngxs/store';
import { CountryService, ECountry } from 'country-service';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, of, switchMap, take, tap } from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';
import { ChatState, CheckCanChat, GetMessagesList, ScrollToBottom, SendMessage, SetActiveStickerPack } from '@app/core/state/chat';
import { GameMessagesState, GetDoubleMessages } from '@app/core/state/game-messages';
import { GamesState } from '@app/core/state/games-store';
import { LayoutState } from '@app/core/state/layout';
import { RainState } from '@app/core/state/rain';
import { UserState } from '@app/core/state/user-store';
import { isSocialInviteMessage, SOCIALS_IN_FOOTER_TOKEN } from '@app/shared/utils';
import type { ChatEngine } from '@app/widgets/chat-v2';

@Injectable()
export class ChatService implements ChatEngine {
  readonly #store = inject(Store);
  readonly #countryService = inject(CountryService);
  readonly #environmentService = inject(EnvironmentService);
  readonly #socials = inject(SOCIALS_IN_FOOTER_TOKEN);

  // Дата первого открытия чата (сервис создаётся один раз, дата не меняется при переоткрытии)
  readonly #socialInviteCreatedAt = new Date();

  readonly isRainAvailable$: Observable<boolean> = this.#store.select(GamesState.isRainAvailable);
  readonly chatRainMessages$: Observable<IMessage[]> = this.#store.select(RainState.chatRainMessages);
  readonly chatDoubleMessages$: Observable<IMessage[]> = this.#store.select(GameMessagesState.doubleMessages);
  readonly chatMessages$: Observable<IMessage[]> = this.#store.select(ChatState.items);
  readonly #isCasesOnlyMode = this.#environmentService.isCasesOnly;
  readonly #isAuth$: Observable<boolean> = this.#store.select(UserState.user).pipe(
    map((user) => !!user),
    distinctUntilChanged(),
  );

  readonly messages$: Observable<IMessageWithSocialValue[]> = combineLatest([
    this.chatMessages$,
    this.#getRainMessages$(),
    this.#getDoubleMessages$(),
    this.#countryService.countryCode$,
  ]).pipe(
    map(([messages, rainMessages, doubleMessages, countryCode]) =>
      this.#buildMessageList(messages, rainMessages, doubleMessages, countryCode as ECountry),
    ),
  );

  #getRainMessages$(): Observable<IMessageWithSocialValue[]> {
    return this.isRainAvailable$.pipe(
      switchMap((isAvailable) => (!this.#isCasesOnlyMode && isAvailable ? this.chatRainMessages$ : of([]))),
    );
  }

  #getDoubleMessages$(): Observable<IMessageWithSocialValue[]> {
    return this.#isAuth$.pipe(
      switchMap((isAuth) => (this.#environmentService.gameIsOn('double', isAuth) ? this.chatDoubleMessages$ : of([]))),
    );
  }

  #buildMessageList(
    messages: IMessageWithSocialValue[],
    rainMessages: IMessageWithSocialValue[],
    doubleMessages: IMessageWithSocialValue[],
    countryCode: ECountry,
  ): IMessageWithSocialValue[] {
    const allMessages: IMessageWithSocialValue[] = [...messages, ...rainMessages, ...doubleMessages];

    if (this.#isCasesOnlyMode) {
      return allMessages.filter((message) => !isSocialInviteMessage(message)).sort(this.#compareByCreatedAt);
    }

    if (!allMessages.some((msg) => isSocialInviteMessage(msg))) {
      allMessages.push(this.#createSocialInviteMessage(countryCode));
    }

    return allMessages.sort(this.#compareByCreatedAt);
  }

  #compareByCreatedAt = (a: IMessageWithSocialValue, b: IMessageWithSocialValue): number => {
    return Date.parse(a.createdAt.toString()) - Date.parse(b.createdAt.toString());
  };

  readonly stickers$: Observable<IStickerPack[]> = this.#store.select(ChatState.stickers);
  readonly activeStickerPack$: Observable<IStickerPack> = this.#store.select(ChatState.activeStickerPack);
  readonly chatId$: Observable<string> = this.#store.select(ChatState.chatId);
  readonly isAnimationPlayed$: Observable<boolean> = this.#store.select(ChatState.isAnimationPlayed);
  readonly user$: Observable<IUserDetailed | null | undefined> = this.#store.select(UserState.user);
  readonly availableGames$: Observable<IGame[]> = this.#store.select(GamesState.availableGames);
  readonly currentGame$: Observable<IGame | null> = this.#store.select(GamesState.currentGame);
  readonly onlineCount$: Observable<number | null> = this.#store.select(LayoutState.online);

  readonly banTimer: BehaviorSubject<string>;
  readonly banTimer$: Observable<string>;

  constructor() {
    this.banTimer = new BehaviorSubject<string>('00:00:00');
    this.banTimer$ = this.banTimer.asObservable();

    // Waits for the auth state that actually enables Double, so a guest chat stays clean.
    this.#isAuth$
      .pipe(
        filter((isAuth) => this.#environmentService.gameIsOn('double', isAuth)),
        take(1),
        tap(() => this.#store.dispatch(new GetDoubleMessages(EWalletType.REAL))),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  addMessage = (msg: IInputMessage): Observable<void> => this.#store.dispatch(new SendMessage(msg));

  setActiveStickerPack = (name: string): Observable<void> => this.#store.dispatch(new SetActiveStickerPack(name));

  getMessagesList = (): Observable<void> => this.#store.dispatch(new GetMessagesList());

  checkCanChat = (): Observable<void> => this.#store.dispatch(new CheckCanChat());

  getOnlineCount = (): Observable<void> => this.#store.dispatch(new CheckCanChat());

  scrollToBottom = (): Observable<void> => this.#store.dispatch(new ScrollToBottom());

  #getSocialLink(type: SocialType): string {
    return this.#socials.find((s) => s.name === type)?.link ?? '';
  }

  #getSocialHandle(link: string): string {
    const parts = link.split('/');
    return `@${parts[parts.length - 1]}`.toUpperCase();
  }

  #createSocialInviteMessage(countryCode: ECountry): ISocialMessage {
    const isRussia = countryCode === ECountry.RU;
    const socialLink = isRussia ? this.#getSocialLink(SocialType.Telegram) : this.#getSocialLink(SocialType.Twitter);
    const socialInviteData: ISocialInviteMessage = {
      type: ChatMessageType.SOCIAL_INVITE,
      title: 'CSGOFAST',
      socialLinks: socialLink,
      socialHandle: this.#getSocialHandle(socialLink),
      buttonText: 'CHAT_WIDGET.SOCIAL.BUTTON_TEXT',
      countryCode: countryCode,
      descriptionOne: 'CHAT_WIDGET.SOCIAL.DESCRIPTION_ONE',
      descriptionTwo: 'CHAT_WIDGET.SOCIAL.DESCRIPTION_TWO',
      icon: isRussia ? 'telegram' : 'socials-x',
      messenger: isRussia ? 'Telegram' : 'X',
    };

    return {
      id: 'social-invite-message',
      user: {
        id: 0,
        name: CS2_NICKNAME,
        avatar: '',
        level: 0,
        profileType: EAccountType.DEMO,
      },
      createdAt: this.#socialInviteCreatedAt,
      value: socialInviteData,
      game: 'common',
    };
  }
}
